import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fmtDate, fmtMinutes, STATUS_COLORS } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Clock, CalendarClock } from "lucide-react";

function Stat({ label, value, hint, testId }) {
  return (
    <Card className="rounded-sm shadow-none p-4">
      <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">{label}</div>
      <div className="font-heading font-black text-2xl mt-1" data-testid={testId}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </Card>
  );
}

function Timeline({ orders, onOpen }) {
  if (!orders.length) return <div className="text-sm text-muted-foreground">Nenhuma OS registrada para este equipamento.</div>;
  return (
    <div className="relative border-l-2 border-orange-500/40 ml-2 space-y-4">
      {orders.map((o) => (
        <button key={o.id} onClick={() => onOpen(o.id)} className="block w-full text-left pl-5 relative group" data-testid={`eq-history-os-${o.number}`}>
          <span className="absolute -left-[7px] top-2 w-3 h-3 rounded-full bg-orange-500" />
          <div className="border rounded-sm p-3 group-hover:border-orange-500 transition-colors">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono-tag font-semibold">OS #{o.number}</span>
              <Badge variant="outline" className={`rounded-sm ${STATUS_COLORS[o.status]}`}>{o.status}</Badge>
              <span className="text-xs text-muted-foreground">{o.maintenance_type} · {o.discipline}</span>
              <span className="text-xs text-muted-foreground ml-auto">{fmtDate(o.created_at)}</span>
            </div>
            <div className="text-sm mt-1 line-clamp-2">{o.description}</div>
            <div className="text-xs text-muted-foreground mt-1">
              Equipe: {o.technician_names?.length ? o.technician_names.join(", ") : o.technician_name || "—"} · Parada: {fmtMinutes(o.downtime_minutes)}
              {o.closed_at && <> · Fechada {fmtDate(o.closed_at)}</>}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function Preventives({ items }) {
  if (!items.length) return <div className="text-sm text-muted-foreground">Sem preventivas agendadas.</div>;
  return items.map((s) => (
    <div key={s.id} className="border rounded-sm p-3 text-sm" data-testid={`eq-history-prev-${s.id}`}>
      <div className="font-semibold">{s.task} <span className="text-xs text-muted-foreground font-normal">a cada {s.interval_days}d</span></div>
      <div className="text-xs text-muted-foreground">Última: {s.last_service_date} · Próxima: {s.next_due_date}</div>
      {(s.completions || []).slice(-3).reverse().map((c) => (
        <div key={c.id} className="text-xs mt-1 bg-secondary rounded-sm p-1.5">{c.date} — {c.technician_names?.join(", ") || c.by}{c.notes && `: ${c.notes}`}</div>
      ))}
    </div>
  ));
}

export default function EquipmentHistory() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/equipments/${id}/history`).then((r) => setData(r.data)); }, [id]);
  if (!data) return <div className="text-muted-foreground">Carregando...</div>;
  const { equipment: eq, kpis } = data;
  return (
    <div className="space-y-6" data-testid="equipment-history">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" onClick={() => nav("/equipamentos")} className="rounded-sm" data-testid="back-btn"><ArrowLeft className="w-4 h-4" /></Button>
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">Histórico do Equipamento · {eq.sector}</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight"><span className="font-mono-tag text-orange-600">{eq.tag}</span> {eq.name}</h1>
          {eq.description && <p className="text-muted-foreground text-sm">{eq.description}</p>}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Total de OS" value={kpis.total} testId="eqh-total" />
        <Stat label="Em aberto" value={kpis.open} testId="eqh-open" />
        <Stat label="Corretivas" value={kpis.corrective} testId="eqh-corrective" />
        <Stat label="MTBF" value={fmtMinutes(kpis.mtbf_minutes)} hint="Entre falhas" testId="eqh-mtbf" />
        <Stat label="MTTR" value={fmtMinutes(kpis.mttr_minutes)} hint="Tempo de reparo" testId="eqh-mttr" />
        <Stat label="Tempo parado" value={fmtMinutes(kpis.total_downtime_minutes)} hint="Total" testId="eqh-downtime" />
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 rounded-sm shadow-none p-6">
          <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2"><Clock className="w-4 h-4 text-orange-500" /> Linha do tempo das OS</h3>
          <Timeline orders={data.orders} onOpen={(oid) => nav(`/os/${oid}`)} />
        </Card>
        <Card className="rounded-sm shadow-none p-6 space-y-3">
          <h3 className="font-heading text-lg font-bold flex items-center gap-2"><CalendarClock className="w-4 h-4 text-orange-500" /> Preventivas</h3>
          <Preventives items={data.preventives} />
        </Card>
      </div>
    </div>
  );
}
